import React, { useCallback, useMemo, useState } from 'react';
import { Link } from "@material-ui/core";
import {useDropzone, DropzoneRootProps, DropzoneOptions, ErrorCode, Accept} from 'react-dropzone';
import Typography from '@material-ui/core/Typography';
import { FieldError } from 'react-hook-form/dist/types';
import ErrorMessage from '../portico/error/error-message';
import { baseStyle, activeStyle, acceptStyle, rejectStyle, useFileUploadStyles } from './file-uploader.styles';
import LinearProgress from '@material-ui/core/LinearProgress';
import mime from 'mime-types';
import ErrorIcon from '@material-ui/icons/Error';
import { withFormRef, withFormError } from './form-integration';
import { cancelUploadDocument } from '../../services/fenergo-service';

interface FileUploadProps extends DropzoneOptions {
    upload: (formData, setProgress: Function, setHideDropzone: Function) => void,
    maxSizeInMB?: number,
    acceptableFileTypesNotes?: string,
    error?: FieldError;
    hasError?: boolean,
    errorMessage?: string,
    cancelUpload: (uploadCancel) => void
    multiple?: boolean,
    maxFiles?: number,
    readOnlyDocument : boolean,
    register?: any,
    index?: number,
    onChange?: any,
    name?: any,
    categoryName?: string
}

const FileUploader: React.FC<FileUploadProps> = ({
    upload,
    accept,
    maxSizeInMB,
    acceptableFileTypesNotes,
    hasError,
    errorMessage,
    cancelUpload,
    maxFiles ,
    multiple ,
    readOnlyDocument,
    register, index, onChange, name, categoryName
}) => {
    const classes = useFileUploadStyles();
    const [progress, setProgress] = useState(0)
    const [hideDropzone, setHideDropzone] = useState(false)

    const onCancel = () => {
        cancelUploadDocument();
        setHideDropzone(false)
        cancelUpload(true);
    }

    const onDrop = useCallback(acceptedFiles => {
        const formData = new FormData()
        acceptedFiles.map((item, i) => {
            formData.append('Data.File', acceptedFiles[i]);
            formData.append('Data.DocumentType', acceptedFiles[i].type);
            formData.append('Data.FriendlyName', acceptedFiles[i].name);
        });
        //Upload-document API call
        if (formData.get('Data.File')) {
            setProgress(Math.floor(Math.random() * 14))
            setHideDropzone(false)
            upload(formData, setProgress, setHideDropzone)
        }

    }, [])

    const fileSizeMB = maxSizeInMB ? maxSizeInMB : 4;

    // TODO - UI Upgrade - Need to check file uploads to test this.
    const acceptableFileTypes = (accept ? String(accept) : ".jpeg, .jpg, .png").split(',')

    const acceptableMIMETypes = acceptableFileTypes.map(fileTypes => mime.lookup(fileTypes.trim())).join(',') as unknown as Accept

    const acceptedFileTypesNote = acceptableFileTypesNotes ? acceptableFileTypesNotes : ".jpeg, .jpg, and .png";

    const {
        getRootProps,
        getInputProps,
        isDragActive,
        isDragAccept,
        isDragReject,
        acceptedFiles,
        fileRejections
    } = useDropzone({ onDrop, accept: acceptableMIMETypes, maxSize: fileSizeMB * Math.pow(1024, 2), multiple: multiple, maxFiles: maxFiles });

    const acceptedItems = acceptedFiles.map(file => file.name)

    const rejectedItems = fileRejections.map(({ file, errors }) => {
        const errorMessage = (e) => {
            const invalidTypeError = `Please check the file type and try again. The allowed file types are ${acceptedFileTypesNote}.`
            const maxSizeError = `The maximum file size is ${fileSizeMB}MB. Please try again.`
            const fileErrors = e.map(error => error.code)

            if (fileErrors.includes(ErrorCode.FileTooLarge))
                return maxSizeError
            else if (fileErrors.includes(ErrorCode.FileInvalidType))
                return invalidTypeError
            else if (fileErrors.length === 2)
                if (fileErrors.includes(ErrorCode.FileInvalidType) && fileErrors.includes(ErrorCode.FileTooLarge))
                    return maxSizeError
        }

        return (
            <>
                <div className={classes.rejectedFileStatus} ><ErrorIcon fontSize="small" className={classes.errorIcon} />{file.name}</div>
                <span className={classes.rejectedFileError} >
                    <Typography variant="caption">{errorMessage(errors)}</Typography>
                </span>
            </>
        )
    })

    const style: DropzoneRootProps = useMemo(() => ({
        ...baseStyle,
        ...(isDragActive ? activeStyle : {}),
        ...(isDragAccept ? acceptStyle : {}),
        ...(isDragReject ? rejectStyle : {})
    }), [
        isDragActive,
        isDragReject,
        isDragAccept
    ]);

    return (
        !readOnlyDocument ?
        <div className="container" ref={register}>
            <div {...getRootProps()} className={`${classes.box}`}>
                {!hideDropzone  && progress <= 100 ? 
                    <>
                        {
                            name === 'document-policy-task-details' 
                            ? (
                                <input {...getInputProps({onChange: (e) => onChange(e, index, `file${index}`)})} {...register(`file${index}`, {  
                                        required: {
                                            value: true,
                                            message: `${categoryName} required`
                                        },
                                    })}/>
                                )   
                            : (
                                <input {...getInputProps()} />
                            )}
                        <p>Drag and drop your file(s) here or &nbsp;
                            <span className={classes.selectingFile}>Select from Computer</span>
                        </p>
                        <p>Max file size: {fileSizeMB}MB</p>
                    </> :
                    <>
                        <div className={classes.progress}>
                            <div className={classes.uploadingFile}>{acceptedItems}</div>
                            <LinearProgress variant="determinate" value={progress} />
                        </div>
                        <Link onClick={onCancel} className={classes.cancel}> Cancel </Link>
                    </>

                }

            </div>
            <aside>
                <Typography variant="caption" className={classes.acceptedTypes}>
                    Accepted file types: {acceptedFileTypesNote}.
                </Typography>
                <div className={classes.uploadedFiles}>
                    {rejectedItems}
                </div>
            </aside>
            {hasError ? <ErrorMessage message={errorMessage} /> : null}
        </div>
        :
        <></>
    );
}

export default withFormRef(withFormError(FileUploader)) as React.FC<FileUploadProps>;
